import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth.store';
import i18n from '@/lib/i18n';
import { endSessionToLogin } from '@/lib/session-boundary';
import { getApiErrorMessage } from '@/lib/toast';
import { getApiErrorStatus, getApiRetryAfterSeconds } from '@/lib/api-error';
import { registerErrorField, type RegisterField } from '@/lib/register-form';
import { safeReturnPath } from '@/lib/return-path';
import type { LoginRequest, RegisterRequest, Login2faRequest } from '@/types/auth';

// These mutations fire toasts outside the React tree, so they use the i18n
// instance directly (not the useTranslation hook) via the eagerly-loaded
// `auth` namespace (T-406).
const t = (key: string, opts?: Record<string, unknown>) => i18n.t(`auth:${key}`, opts);

/**
 * Password sign-in. A throttled attempt (429) is handed to `onThrottled` with the wait in seconds so the
 * page can show one inline notice; without a handler it stays a toast.
 */
/**
 * The page the visitor asked for before being sent to /login (router state `from`, set by the route
 * guards), or the dashboard. Only a plain in-app path is used (POS-I186).
 */
function useReturnPath(): string {
  const location = useLocation();
  return safeReturnPath((location.state as { from?: unknown } | null)?.from);
}

export function useLogin(onRequires2fa?: (twoFactorToken: string) => void, onThrottled?: (retryAfterSeconds: number) => void) {
  const navigate = useNavigate();
  const returnTo = useReturnPath();
  const setAuth = useAuthStore((s) => s.setAuth);

  return useMutation({
    mutationFn: (data: LoginRequest) => authService.login(data),
    onSuccess: (data) => {
      if ('requires2fa' in data) {
        onRequires2fa?.(data.twoFactorToken);
        return;
      }
      setAuth(data.user, data.tenant, data.accessToken, data.refreshToken);
      toast.success(t('welcomeBackName', { name: data.user.firstName }));
      navigate(returnTo);
    },
    onError: (error: unknown) => {
      const wait = getApiErrorStatus(error) === 429 ? getApiRetryAfterSeconds(error) : undefined;
      if (wait !== undefined && onThrottled) {
        onThrottled(wait);
        return;
      }
      toast.error(getApiErrorMessage(error, t('loginFailed')));
    },
  });
}

export function useLogin2fa(onExpired?: () => void) {
  const navigate = useNavigate();
  const returnTo = useReturnPath();
  const setAuth = useAuthStore((s) => s.setAuth);

  return useMutation({
    mutationFn: (data: Login2faRequest) => authService.login2fa(data),
    onSuccess: (data) => {
      setAuth(data.user, data.tenant, data.accessToken, data.refreshToken);
      toast.success(t('welcomeBackName', { name: data.user.firstName }));
      navigate(returnTo);
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('verificationFailed'));
      toast.error(message);
      // The short-lived 2FA token expired — send the user back to the credentials
      // form so they can re-authenticate and get a fresh challenge.
      if (getApiErrorStatus(error) === 401 && /expired|log in again/i.test(message)) {
        onExpired?.();
      }
    },
  });
}

/**
 * Register. A refusal that belongs to one field (Business ID taken, a limit, a weak password) is handed to
 * `onFieldError` so the page can show it under that field; anything else stays a toast.
 */
export function useRegister(onFieldError?: (field: RegisterField, message: string) => void) {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  return useMutation({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: (data) => {
      setAuth(data.user, data.tenant, data.accessToken, data.refreshToken);
      toast.success(t('accountCreated'));
      navigate('/dashboard');
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, t('registrationFailed'));
      const status = getApiErrorStatus(error);
      const field = status !== undefined && status < 500 ? registerErrorField(message) : null;
      if (field && onFieldError) {
        onFieldError(field, message);
        return;
      }
      toast.error(message);
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => authService.logout(),
    // Full page navigation (POS-I109): no in-memory state may survive the user's logout.
    onSuccess: () => endSessionToLogin(),
    onError: () => endSessionToLogin(),
  });
}
